// OverUnderTool.jsx
import { useEffect, useRef, useState } from "react";
import { FaTelegramPlane, FaWhatsapp } from "react-icons/fa";

const APP_ID = 124809;

const VOLATILITIES = [
  { name: "Volatility 10 (1s) Index", symbol: "1HZ10V" },
  { name: "Volatility 10 Index", symbol: "R_10" },
  { name: "Volatility 15 (1s) Index", symbol: "1HZ15V" },
  { name: "Volatility 25 (1s) Index", symbol: "1HZ25V" },
  { name: "Volatility 25 Index", symbol: "R_25" },
  { name: "Volatility 30 (1s) Index", symbol: "1HZ30V" },
  { name: "Volatility 50 (1s) Index", symbol: "R_50" },
  { name: "Volatility 75 (1s) Index", symbol: "1HZ75V" },
  { name: "Volatility 75 Index", symbol: "R_75" },
  { name: "Volatility 90 (1s) Index", symbol: "1HZ90V" },
  { name: "Volatility 100 (1s) Index", symbol: "1HZ100V" },
  { name: "Volatility 100 Index", symbol: "R_100" },
];

// Build targets dynamically: OVER/UNDER 1–9
const TARGETS = [];
for (let n = 1; n <= 9; n++) {
  TARGETS.push({ type: "OVER", value: n });
  TARGETS.push({ type: "UNDER", value: n });
}

const scaleConfidence = raw => Math.round(85 + (raw / 100) * 14);

export default function OverUnderTool() {
  const [ticks, setTicks] = useState({});
  const [signals, setSignals] = useState({});
  const [phase, setPhase] = useState("countdown");
  const [timer, setTimer] = useState(15);

  const ticksRef = useRef({});

  useEffect(() => {
    ticksRef.current = ticks;
  }, [ticks]);

  // ─── WEBSOCKETS ────────────────────────────────────────
  useEffect(() => {
    const sockets = VOLATILITIES.map(({ symbol }) => {
      const ws = new WebSocket(
        `wss://ws.binaryws.com/websockets/v3?app_id=${APP_ID}`
      );

      ws.onopen = () =>
        ws.send(JSON.stringify({ ticks: symbol, subscribe: 1 }));

      ws.onmessage = e => {
        const res = JSON.parse(e.data);

        if (res.tick?.quote !== undefined) {
          const digit = Math.floor(res.tick.quote * 10) % 10;

          setTicks(prev => {
            const updated = {
              ...prev,
              [symbol]: [...(prev[symbol] || []), digit].slice(-50),
            };

            if (phase === "countdown") {
              generateSignal(symbol, updated[symbol]);
            }

            return updated;
          });
        }
      };

      return ws;
    });

    return () => sockets.forEach(ws => ws.close());
  }, [phase]);

  // ─── TIMER + PHASE CYCLE ───────────────────────────────
  useEffect(() => {
    const generateAllSignals = () => {
      VOLATILITIES.forEach(({ symbol }) =>
        generateSignal(symbol, ticksRef.current[symbol])
      );
    };

    const interval = setInterval(() => {
      setTimer(prev => {
        if (prev <= 1) {
          if (phase === "countdown") {
            setPhase("entry");
            return 5;
          }
          if (phase === "entry") {
            setPhase("trade");
            return 5;
          }
          if (phase === "trade") {
            setPhase("countdown");
            generateAllSignals();
            return 15;
          }
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [phase]);

  // ─── NEW SIGNAL GENERATOR (LAST 10 + ALL 1–9) ─────────────────
  const generateSignal = (symbol, history = []) => {
    const recent = history.slice(-10);

    if (recent.length === 0) return;

    let bestScore = -1;
    let bestType = "OVER";
    let bestValue = 1;

    TARGETS.forEach(({ type, value }) => {
      const count = recent.filter(d =>
        type === "OVER" ? d > value : d < value
      ).length;

      const score = count / recent.length;

      if (score > bestScore) {
        bestScore = score;
        bestType = type;
        bestValue = value;
      }
    });

    // Fallback if something weird happens
    if (bestScore === -1) {
      const avg =
        recent.reduce((a, b) => a + b, 0) / recent.length;
      bestType = avg > 4.5 ? "OVER" : "UNDER";
      bestValue = Math.round(avg);
      bestScore = 0.5;
    }

    const confidence = scaleConfidence(bestScore * 100);
    const label = `${bestType} ${bestValue}`;

    setSignals(prev => {
      const prevSignal = prev[symbol];
      if (prevSignal?.label === label) return prev;

      return {
        ...prev,
        [symbol]: {
          type: bestType,
          value: bestValue,
          label,
          confidence,
        },
      };
    });
  };

  // ─── RENDER ────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#050814] text-white px-5 py-10 flex flex-col items-center">
      <div className="max-w-5xl text-center mb-10">
        <h1 className="text-3xl md:text-4xl font-extrabold text-purple-400 mb-2">
          Over / Under Tool
        </h1>
        <p className="text-gray-300 max-w-3xl mx-auto text-sm md:text-base">
          Live Over/Under last-digit predictions with confidence
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 w-full max-w-7xl">
        {VOLATILITIES.map(({ name, symbol }) => {
          const signal = signals[symbol] || {};
          const showEntry = phase === "entry" && signal.label;
          const showTrade = phase === "trade" && signal.label;
          const isOver = signal.type === "OVER";

          return (
            <div
              key={symbol}
              className="bg-[#0B1020] border border-purple-900/40 rounded-xl p-4 flex flex-col items-center text-center shadow-md"
            >
              <h3 className="text-sm md:text-base font-semibold text-purple-300 mb-2">
                {name}
              </h3>

              {showEntry || showTrade ? (
                <>
                  <div
                    className={`w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center text-2xl md:text-3xl font-extrabold mb-2 ${
                      isOver
                        ? "bg-gradient-to-tr from-green-500 to-emerald-400"
                        : "bg-gradient-to-tr from-red-500 to-rose-400"
                    } text-white`}
                  >
                    {signal.type}
                  </div>

                  <p className="text-yellow-300 text-sm md:text-base font-semibold mb-1">
                    {signal.label}
                  </p>

                  <p className="text-emerald-400 text-sm md:text-base font-bold mb-1">
                    {signal.confidence}%
                  </p>

                  {showEntry ? (
                    <p className="text-sm md:text-base text-purple-400">
                      Entry in {timer}s
                    </p>
                  ) : (
                    <p className="text-lg md:text-xl text-green-400 font-extrabold animate-pulse">
                      TRADE
                    </p>
                  )}
                </>
              ) : (
                <>
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#050814] border border-purple-900/40 flex items-center justify-center text-xl md:text-2xl font-bold text-gray-400 mb-2">
                    {timer}s
                  </div>
                  <p className="text-xs md:text-sm text-gray-400">
                    Next prediction
                  </p>
                </>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-10 flex gap-6 text-3xl">
        <a
          href="https://t.me/+Wyue3S8hoStiMjQ0"
          target="_blank"
          rel="noreferrer"
          className="text-purple-400 hover:text-purple-300"
        >
          <FaTelegramPlane />
        </a>
        <a
          href="https://wa.me/254117623556"
          target="_blank"
          rel="noreferrer"
          className="text-green-400 hover:text-green-300"
        >
          <FaWhatsapp />
        </a>
      </div>
    </div>
  );
}
